/**
 * LOGS-01: Bounded ring buffer for captured console and network log entries.
 *
 * Limits (D-10):
 * - 50 console entries maximum
 * - 50 network entries maximum
 * - On overflow, the oldest entry is dropped (FIFO eviction)
 *
 * Types are imported from @support-system/shared (single source of truth — D-03).
 */
import type { ConsoleLogEntry, NetworkLogEntry, LogBundle } from '@support-system/shared';

const CONSOLE_CAP = 50;
const NETWORK_CAP = 50;

/**
 * Bounded ring buffer holding the last N console + network entries.
 *
 * Usage:
 * ```ts
 * const buf = new LogBuffer();
 * buf.pushConsole({ ts: Date.now(), level: 'error', message: 'oops' });
 * buf.pushNetwork({ ts: Date.now(), method: 'GET', url: '/api/[id]', status: 200, duration: 42 });
 * const snapshot: LogBundle = buf.snapshot();
 * ```
 */
export class LogBuffer {
    private readonly _console: ConsoleLogEntry[] = [];
    private readonly _network: NetworkLogEntry[] = [];

    /**
     * Append a console entry. If the buffer is full, the oldest entry is evicted.
     */
    pushConsole(entry: ConsoleLogEntry): void {
        if (this._console.length >= CONSOLE_CAP) {
            this._console.shift(); // drop oldest
        }
        this._console.push(entry);
    }

    /**
     * Append a network entry. If the buffer is full, the oldest entry is evicted.
     */
    pushNetwork(entry: NetworkLogEntry): void {
        if (this._network.length >= NETWORK_CAP) {
            this._network.shift(); // drop oldest
        }
        this._network.push(entry);
    }

    /**
     * Return a shallow-copy snapshot of the current buffer state as a LogBundle.
     * The snapshot is safe to serialize and ship without further mutation.
     */
    snapshot(): LogBundle {
        return {
            console: [...this._console],
            network: [...this._network],
        };
    }
}
